/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IObjectWithKey, ISelection, Selection, SelectionMode } from '@fluentui/react/lib/DetailsList';
import { action, observable } from 'mobx';
import { IntentPageOpsStore } from 'src/luis/appShell/buildPane/intentPage/stores/IntentPageOpsStore';
import { SidePaneEntitySelectionStore } from 'src/luis/appShell/buildPane/intentPage/stores/SidePaneEntitySelectionStore';
import { UtteranceObservable } from 'src/luis/appShell/buildPane/intentPage/types';
import { LabelingStore } from 'src/luis/projects/utterances/stores/LabelingStore';
import { UtteranceFiltersStore } from 'src/verticals/utterancesFilters/stores/UtteranceFiltersStore';

type BladeType = 'viewOptions' | 'entityPalette' | 'none';

export type PageSize = 10 | 20 | 50;

/**
 * Reactive store for intent page UI interaction states.
 */
export class IntentPageUIConfigStore {
    public readonly selection: ISelection<IObjectWithKey>;

    public readonly sidePaneEntitySelectionStore: SidePaneEntitySelectionStore;

    public readonly intentFiltersStore: UtteranceFiltersStore;

    public readonly labelingStore: LabelingStore;

    public readonly opsStore: IntentPageOpsStore;

    @observable
    public activeBladeType: BladeType;

    @observable
    public currentPageSize: PageSize;

    @observable
    public searchQuery: string;

    constructor() {
        this.intentFiltersStore = new UtteranceFiltersStore();
        this.sidePaneEntitySelectionStore = new SidePaneEntitySelectionStore();
        this.labelingStore = new LabelingStore();
        this.opsStore = new IntentPageOpsStore();
        this.selection = new Selection({
            onSelectionChanged: action(() => {
                this.opsStore.selectedItems = <UtteranceObservable[]>this.selection.getSelection();
            }),
            selectionMode: SelectionMode.multiple
        });
        this.activeBladeType = 'none';
        this.currentPageSize = 10;
        this.searchQuery = '';
    }

    /**
     * Setter for the number of utterances per page.
     */
    @action
    public setCurrentPageSize(pageSize: PageSize) {
        this.currentPageSize = pageSize;
    }

    /**
     * Setter for the current active blade.
     */
    @action
    public setActiveBladeType(bladeType: BladeType) {
        this.activeBladeType = bladeType;
    }

    /**
     * Setter for the search query.
     */
    @action
    public setSearchQuery(query: string) {
        this.searchQuery = query;
    }
}
